import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  Zap, 
  Bot, 
  Users, 
  Cpu, 
  CheckCircle2, 
  Clock,
  AlertCircle,
  Star
} from "lucide-react";
import { Button } from "../../components/ui/button";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OptimizationStep = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [monitoringStatus, setMonitoringStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const caseRes = await axios.get(`${API}/cases/my-case`);
      setCaseData(caseRes.data);
      
      if (caseRes.data?.id) {
        const monitorRes = await axios.get(`${API}/cases/${caseRes.data.id}/monitoring`);
        setMonitoringStatus(monitorRes.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMonitoring = async (activate) => {
    if (!caseData?.id) return;
    
    setActivating(true);
    try {
      await axios.post(`${API}/cases/${caseData.id}/monitoring`, { activate });
      toast.success(activate ? "¡Optimización activada!" : "Optimización desactivada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado");
    } finally {
      setActivating(false);
    }
  };

  const getStatusConfig = () => {
    if (!monitoringStatus?.monitoring_active) {
      return {
        icon: Zap,
        color: "text-navy-400",
        bg: "bg-navy-100",
        title: "No Activado",
        description: "El servicio de optimización no está activo"
      };
    }
    
    switch (monitoringStatus.monitoring_status) {
      case "monitoring":
        return {
          icon: Cpu,
          color: "text-purple-600",
          bg: "bg-purple-100",
          title: "Monitoreando 24/7",
          description: "Nuestro sistema está buscando fechas más cercanas"
        };
      case "optimized_found":
        return {
          icon: Star,
          color: "text-gold-600",
          bg: "bg-gold-100",
          title: "¡Fecha Optimizada!",
          description: "Se encontró una fecha más cercana"
        };
      default:
        return {
          icon: Clock,
          color: "text-amber-600",
          bg: "bg-amber-100",
          title: "En Espera",
          description: "Preparando el sistema de monitoreo"
        };
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="mb-4 text-navy-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Camino al Éxito
          </Button>
          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
            Optimización de Cita
          </h1>
          <p className="text-navy-600">
            Servicio premium de monitoreo inteligente para adelantar tu entrevista.
          </p>
        </motion.div>

        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`card-elevated p-8 mb-8 border-l-4 ${
            monitoringStatus?.monitoring_active ? "border-purple-500" : "border-navy-300"
          }`}
        >
          <div className="flex items-center gap-6">
            <div className={`w-20 h-20 ${statusConfig.bg} rounded-2xl flex items-center justify-center`}>
              <StatusIcon className={`w-10 h-10 ${statusConfig.color}`} />
            </div>
            <div>
              <h2 className={`font-heading text-2xl font-bold ${statusConfig.color}`}>
                {statusConfig.title}
              </h2>
              <p className="text-navy-600 mt-1">{statusConfig.description}</p>
              {monitoringStatus?.details?.last_checked && (
                <p className="text-sm text-navy-400 mt-2">
                  Última verificación: {new Date(monitoringStatus.details.last_checked).toLocaleString('es-ES')}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-elevated p-6 mb-8"
        >
          <h3 className="font-heading text-lg font-bold text-navy-900 mb-6">
            ¿Qué incluye este servicio?
          </h3>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Cpu className="w-7 h-7 text-purple-600" />
              </div>
              <h4 className="font-semibold text-navy-900">Inteligencia Artificial</h4>
              <p className="text-sm text-navy-500 mt-1">
                Detecta patrones de liberación de citas
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Bot className="w-7 h-7 text-blue-600" />
              </div>
              <h4 className="font-semibold text-navy-900">Bots de Monitoreo</h4>
              <p className="text-sm text-navy-500 mt-1">
                Verificación continua del sistema
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-7 h-7 text-emerald-600" />
              </div>
              <h4 className="font-semibold text-navy-900">Supervisión Humana</h4>
              <p className="text-sm text-navy-500 mt-1">
                Equipo disponible 24/7
              </p>
            </div>
          </div>
        </motion.div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900">Información Importante</h4>
              <p className="text-sm text-amber-800 mt-1">
                Este servicio <strong>incrementa las probabilidades</strong> de encontrar una fecha más cercana, 
                pero <strong>no garantiza resultados específicos</strong>. La disponibilidad de citas depende 
                del sistema consular.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          {!monitoringStatus?.monitoring_active ? (
            <Button
              onClick={() => toggleMonitoring(true)}
              disabled={activating}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg"
              data-testid="activate-monitoring-btn"
            >
              {activating ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Activar Optimización
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-purple-100 rounded-full">
                <Zap className="w-5 h-5 text-purple-600" />
                <span className="text-purple-700 font-medium">Optimización Activa</span>
              </div>
              <div>
                <Button
                  onClick={() => toggleMonitoring(false)}
                  variant="outline"
                  className="text-navy-500"
                  data-testid="deactivate-monitoring-btn"
                >
                  Desactivar servicio
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Continue Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex justify-end"
        >
          <Button
            onClick={() => navigate("/dashboard")}
            className="bg-gold-600 hover:bg-gold-700 text-white"
          >
            Volver al Dashboard
          </Button>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default OptimizationStep;
